import React, { memo, useEffect, useState } from 'react';
import {
  Box,
  DateAndTimePicker,
  Icon,
  InlineMessage,
  Typography,
} from '@saviynt/design-system';
import { addYears, format, isValid } from 'date-fns';

import { CF_NEU_BLD, CF_SEC_BLD } from '../../../constants';
import { useInvertedLocalize } from '../../../hooks';

import { getUTCEpochFromLocalDate } from './utils';

import './EndDatePicker.css';

// 2025-07-24 01:35:09
interface EndDatePickerProps {
  isCritical: boolean;
  startDate?: string | null;
  currentEndDate?: string | null;
  globalDateFormat: string;
  setIsCritical: (state: boolean) => void;
  setEpoch: React.Dispatch<
    React.SetStateAction<{
      startEpoch: number | null;
      endEpoch: number | null;
    }>
  >;
}

/**
 * EndDatePicker
 */
function EndDatePicker({
  isCritical,
  startDate,
  currentEndDate,
  globalDateFormat,
  setIsCritical,
  setEpoch,
}: EndDatePickerProps) {
  const l = useInvertedLocalize();

  const [startDateTime, setStartDateTime] = useState<Date | null>(null);

  const minDate =
    startDate && isValid(new Date(startDate))
      ? new Date(startDate)
      : new Date();
  const rangeMaxDate = addYears(minDate, 999);

  useEffect(() => {
    setEpoch({
      startEpoch: null,
      endEpoch: startDateTime ? getUTCEpochFromLocalDate(startDateTime) : null,
    });
  }, [startDateTime, setEpoch]);

  return (
    <Box tag='div' className='EndDatePicker'>
      {currentEndDate && (
        <Box tag='div' className='EndDatePicker-currentDateWrapper'>
          <Typography kind='h3' color={CF_NEU_BLD}>
            {l('Current End Date')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_BLD}>
            {isValid(new Date(currentEndDate))
              ? format(new Date(currentEndDate), globalDateFormat)
              : currentEndDate}
          </Typography>
        </Box>
      )}
      <DateAndTimePicker
        kind='dateAndTimeStart'
        label={l('New End Date')}
        isRequired={startDateTime === null || isCritical}
        isCritical={isCritical}
        setIsCritical={setIsCritical}
        CriticalHelperText={
          <InlineMessage
            text={l('This required field is incomplete')}
            colorTheme='critical'
            size='small'
            leftIcon={<Icon kind='AlertCriticalFilled' />}
          />
        }
        startDate={startDateTime}
        setStartDate={setStartDateTime}
        maxDate={rangeMaxDate}
        minDate={minDate}
        dateFormat='MM/dd/yyyy, h:mm aa'
        portalId='root'
      />
    </Box>
  );
}

EndDatePicker.defaultProps = {
  startDate: null,
  currentEndDate: null,
};

export default memo(EndDatePicker);
